'use client';

import { useTranslations } from 'next-intl';
import { GmDailyStreakCard } from '@/components/entities/gamification/GmDailyStreakCard';
import { GmDailyStreakSkeleton } from '@/components/entities/gamification/skeletons/GmDailyStreakSkeleton';
import { Icon } from '@/components/entities/Sprite';
import { SliderWrapper } from '@/components/shared/SliderWrapper';
import openModal from '@/modals/utils/openModal';
import { cn } from '@/utils/cn';
import { useGamificationStreak } from '../hooks/useGamificationStreak';
import { getStreakCardVariant } from '../utils/streak.utils';
import { StreakEmptyState } from './StreakEmptyState';

export const GamificationStreak = () => {
  const t = useTranslations('gamification.daily-streak');
  const {
    currentStreak,
    isCountedToday,
    dailyAwards,
    hasNextPage,
    isPending,
    hasStreakData,
    showStartCard,
    headingRef,
    loaderRef,
    setScrollRoot,
  } = useGamificationStreak();

  const handleOpenInfoModal = () => {
    openModal('gm-daily-streak');
  };

  if (isPending) {
    return <GmDailyStreakSkeleton />;
  }

  if (!hasStreakData) {
    return null;
  }

  return (
    <div data-name="gamification-streak" className={cn('flex flex-col', showStartCard && 'md:mb-6')}>
      <div className="flex relative z-2 items-center gap-1.5">
        <h2 ref={headingRef} className="text-sm font-semibold md:font-medium leading-5.5 text-white md:text-lg">
          {t('title')}
        </h2>
        <button
          type="button"
          className="relative z-20"
          onClick={handleOpenInfoModal}
        >
          <Icon
            name="info"
            className="size-3.25 hover:text-white/80 duration-300 transition-colors shrink-0 text-white"
            aria-label={t('utc-info')}
          />
        </button>
      </div>

      {showStartCard
        ? (
            <StreakEmptyState />
          )
        : (
            <div data-name="gamification-streak-slider" className="relative max-lg:-mx-4 max-lg:overflow-x-clip lg:-mx-10 3xl:mr-0 3xl:pr-0">
              <SliderWrapper ref={setScrollRoot} className="-my-6 pb-5.25 pt-8.75 md:pt-12 md:pb-12 lg:pl-10 lg:pointer-events-none lg:cursor-default lg:active:cursor-default">
                <div className="flex w-max lg:w-full px-4 lg:px-0 gap-2 grow flex-1 max-lg:min-w-[stretch]">
                  <div className="flex w-fit gap-2 lg:pr-10 3xl:pr-0">
                    {dailyAwards.map(({ day, xp, awardId }) => {
                      return (
                        <div key={`${awardId}-${day}`} data-streak-day={day} className="shrink-0 lg:pointer-events-auto lg:cursor-grab lg:active:cursor-grabbing">
                          <GmDailyStreakCard
                            xp={xp}
                            day={day}
                            variant={getStreakCardVariant(day, currentStreak, isCountedToday)}
                          />
                        </div>
                      );
                    })}
                    {hasNextPage && <div ref={loaderRef} className="w-px shrink-0" aria-hidden="true" />}
                  </div>
                </div>
              </SliderWrapper>
            </div>
          )}
    </div>
  );
};
